/* ============================================================
   Cart limits (LIVN)
   Enforces product.metafields.custom.max_cart_qty:
   - max N units of that product per cart, 1 unit per line (= per colour)
   - newest lines are kept, surplus is removed (cart/update.js)
   - runs after every cart mutation (any request to /cart/add|change|update|clear,
     detected via PerformanceObserver — independent of fetch/XHR wrappers of apps),
     on page load, when the cart drawer opens and right before checkout
   - limits come from /cart?view=limits (templates/cart.limits.liquid)
   ============================================================ */
(() => {
  'use strict';

  if (window.__cartLimitsLoaded) return;
  window.__cartLimitsLoaded = true;

  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const SELF_MARK = 'cart_limits=1';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const nativeFetch = window.fetch.bind(window);

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let running = null;
  let rerun = false;
  let timer = null;
  let approvedSubmit = false;

  function getSectionIds() {
    const cartMini = document.querySelector('xo-cart-mini');
    if (cartMini && cartMini.id) return [cartMini.id, 'cart-icon-bubble'];
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function pushCartSections(sections, size) {
    if (typeof window.xoStore === 'undefined' || !sections || !Object.keys(sections).length) return;
    window.xoStore.set('xo-cart', function (prev) {
      return Object.assign({}, prev, {
        sections: sections,
        size: size != null ? size : (prev.size || 0)
      });
    })('xo-cart/updateCartSections');
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function toast(text) {
    let el = document.getElementById('cart-limit-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cart-limit-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText =
        'position:fixed;left:50%;top:16px;transform:translateX(-50%);z-index:2147483647;' +
        'width:max-content;max-width:min(92vw,420px);padding:12px 16px;border-radius:10px;' +
        'background:#151413;color:#fff;font:300 14px/1.35 Nexa,sans-serif;text-align:center;' +
        'box-shadow:0 6px 24px rgba(0,0,0,.18);opacity:0;transition:opacity .25s;pointer-events:none;';
      document.body.appendChild(el);
    }
    el.textContent = text;
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    clearTimeout(el.__hide);
    el.__hide = setTimeout(() => { el.style.opacity = '0'; }, 5000);
  }

  async function readLimits() {
    // No "Accept: application/json" here — Shopify would answer with plain cart JSON
    // instead of rendering templates/cart.limits.liquid.
    const res = await nativeFetch(routeRoot() + 'cart?view=limits', {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!res.ok) throw new Error('cart?view=limits HTTP ' + res.status);
    return JSON.parse(await res.text());
  }

  // Cart order = newest first → newest lines are kept.
  function computeUpdates(data) {
    const updates = {};
    const used = {};
    let limitHit = 0;
    (data.items || []).forEach((line) => {
      const limit = parseInt(line.limit, 10) || 0;
      if (limit <= 0) return;
      const already = used[line.product_id] || 0;
      const allowed = Math.max(0, Math.min(1, limit - already));
      if (line.quantity !== allowed) {
        updates[line.key] = allowed;
        limitHit = limit;
      }
      used[line.product_id] = already + allowed;
    });
    return { updates: updates, limitHit: limitHit };
  }

  function enforce() {
    if (running) {
      rerun = true;
      return running;
    }
    running = (async () => {
      let changed = false;
      try {
        const result = computeUpdates(await readLimits());
        if (Object.keys(result.updates).length) {
          const res = await nativeFetch(routeRoot() + 'cart/update.js?' + SELF_MARK, {
            method: 'POST',
            headers: JSON_HEADERS,
            credentials: 'same-origin',
            body: JSON.stringify({
              updates: result.updates,
              sections: getSectionIds(),
              sections_url: window.location.pathname
            })
          });
          if (!res.ok) throw new Error('cart/update.js HTTP ' + res.status);
          const cart = await res.json();
          changed = true;
          pushCartSections(cart.sections, cart.item_count);
          document.dispatchEvent(new CustomEvent('cartLimits:enforced', { detail: { updates: result.updates } }));
          toast('Maximal ' + result.limitHit + ' Muster pro Bestellung (je Farbe 1 Stück) – dein Warenkorb wurde angepasst.');
          if (isCartPage()) setTimeout(() => window.location.reload(), 1500);
        }
      } catch (err) {
        console.warn('[cart-limits]', err);
      } finally {
        running = null;
        if (rerun) {
          rerun = false;
          schedule(0);
        }
      }
      return changed;
    })();
    return running;
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(enforce, delay == null ? 150 : delay);
  }

  // 1) Watch cart mutations (fetch, XHR, beacons — whoever sends them)
  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const url = entry.name || '';
        if (url.indexOf(SELF_MARK) === -1 && CART_MUTATION.test(url)) schedule();
      });
    }).observe({ type: 'resource', buffered: false });
  } catch (e) {
    console.warn('[cart-limits] PerformanceObserver unavailable', e);
  }

  // 2) Lifecycle checks
  document.addEventListener('cart:updated', () => schedule());
  window.addEventListener('pageshow', (e) => { if (e.persisted) schedule(0); });
  document.addEventListener('click', (e) => {
    if (e.target && e.target.closest && e.target.closest("[xo-name='cart']")) schedule(0);
  }, true);

  // 3) Checkout guard (capture phase, runs before theme handlers)
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = e.submitter || null;
    const action = form.getAttribute('action') || '';
    const toCheckout = /\/checkout/.test(action) || (submitter && submitter.getAttribute('name') === 'checkout');
    if (!toCheckout) return;
    if (approvedSubmit) {
      approvedSubmit = false;
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    clearTimeout(timer);
    const changed = await enforce();
    if (changed) return; // cart was corrected → customer reviews it first
    approvedSubmit = true;
    setTimeout(() => { approvedSubmit = false; }, 0);
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
    } else {
      HTMLFormElement.prototype.submit.call(form);
    }
  }, true);

  document.addEventListener('click', async (e) => {
    const link = e.target && e.target.closest ? e.target.closest('a[href*="/checkout"]') : null;
    if (!link || e.defaultPrevented) return;
    e.preventDefault();
    const changed = await enforce();
    if (!changed) window.location.href = link.href;
  }, true);

  // 4) Initial check + small debug/API hook
  window.CartLimits = { check: enforce };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(0));
  } else {
    schedule(0);
  }
})();